import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Loader2, Pencil, Check, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { opsApi, pct } from "../../lib/api";
import NeedNote from "@/components/NeedNote";

const FIELDS = [
  ["gc_submission_day", "GC submission day", "number"],
  ["retention_percent", "Retention %", "number"],
  ["payment_terms_days", "Payment terms (net days)", "number"],
  ["stored_material_rules", "Stored material rules", "text"],
  ["final_billing_requirements", "Final billing requirements", "text"],
  ["gc_forms", "GC-specific forms", "text"],
  ["notes", "Billing notes", "text"],
];

const Row = ({ label, children }) => (
  <div className="flex items-baseline gap-3">
    <span className="w-44 shrink-0 text-xs font-medium text-slate-500">{label}</span>
    <span className="min-w-0 flex-1 text-sm text-slate-900">{children}</span>
  </div>
);

/** FIN-1 — the contract's billing terms, the GC's requirements and the generated calendar. */
export default function BillingProfile({ project }) {
  const [d, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await opsApi.billingProfile(project.id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the billing profile"); }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  if (!d) return <Loader2 className="h-4 w-4 animate-spin text-slate-400" />;
  const p = d.profile;

  const save = async (patch) => {
    setBusy(true);
    try { setData(await opsApi.saveBillingProfile(project.id, patch)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save that"); }
    finally { setBusy(false); }
  };

  const toggleReq = async (key, field, value) => {
    const rows = d.requirements.map((r) => ({ ...r }));
    const found = rows.find((r) => r.key === key);
    if (field === "on") {
      const next = found ? rows.filter((r) => r.key !== key)
        : [...rows, { key, cadence: d.requirement_types.find((t) => t.key === key)?.cadence }];
      return persistReqs(next);
    }
    if (found) found[field] = value;
    return persistReqs(rows);
  };

  const persistReqs = async (rows, asDefault = false) => {
    setBusy(true);
    try {
      setData(await opsApi.saveBillingRequirements(project.id, rows, asDefault));
      if (asDefault) toast.success("Saved as this GC's default set");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the requirements"); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const res = await opsApi.regenerateBillingCalendar(project.id);
      setData({ ...d, periods: res.periods, current_period: res.current_period });
      toast.success(`${res.periods.length} billing periods`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build the calendar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="ops-billing-profile">
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-4 flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-slate-900">Billing Profile</h3>
          {d.can_edit && (
            <Button variant={editing ? "default" : "outline"} size="sm" disabled={busy}
                    onClick={() => setEditing((v) => !v)} data-testid="ops-billing-profile-edit">
              {editing ? <><Check className="mr-1.5 h-3.5 w-3.5" /> Done</>
                : <><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</>}
            </Button>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Row label="Billing required">
              {editing ? (
                <Checkbox checked={p.billing_required} data-testid="ops-billing-required"
                          onCheckedChange={(v) => save({ billing_required: !!v })} />
              ) : (p.billing_required ? "Yes" : "No")}
            </Row>
            <Row label="Frequency">
              {editing ? (
                <select value={p.frequency} data-testid="ops-billing-frequency"
                        onChange={(e) => save({ frequency: e.target.value })}
                        className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm">
                  {d.frequencies.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              ) : d.frequencies.find((f) => f.key === p.frequency)?.label}
            </Row>
            <Row label="Pay-app platform">
              {editing ? (
                <select value={p.platform} data-testid="ops-billing-platform"
                        onChange={(e) => save({ platform: e.target.value })}
                        className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm">
                  {d.platforms.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              ) : d.platforms.find((f) => f.key === p.platform)?.label}
            </Row>
            <Row label="Billing contact">
              {editing ? (
                <select value={p.billing_contact_id || ""} data-testid="ops-billing-contact"
                        onChange={(e) => save({ billing_contact_id: e.target.value })}
                        className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm">
                  <option value="">Nobody yet</option>
                  {d.contacts.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.role ? ` — ${c.role}` : ""}</option>
                  ))}
                </select>
              ) : (d.contacts.find((c) => c.id === p.billing_contact_id)?.name
                || <NeedNote>No billing contact recorded</NeedNote>)}
            </Row>
          </div>

          <div className="space-y-2">
            {FIELDS.map(([key, label, type]) => (
              <Row key={key} label={label}>
                {editing ? (
                  <Input type={type} defaultValue={p[key] ?? ""} className="h-8 text-sm"
                         data-testid={`ops-billing-${key}`}
                         onBlur={(e) => {
                           const v = type === "number" ? Number(e.target.value) : e.target.value;
                           if (v !== p[key]) save({ [key]: v });
                         }} />
                ) : (key === "retention_percent" ? pct(p[key])
                  : (p[key] || <span className="text-slate-400">—</span>))}
              </Row>
            ))}
            <Row label="Certified payroll">
              {p.certified_payroll_required ? "Required (prevailing wage)" : "Not required"}
            </Row>
            <Row label="Notarization">
              {editing ? (
                <Checkbox checked={p.notarization_required} data-testid="ops-billing-notary"
                          onCheckedChange={(v) => save({ notarization_required: !!v })} />
              ) : (p.notarization_required ? "Required" : "Not required")}
            </Row>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-slate-900">Billing Requirements</h3>
          {d.can_edit && (
            <Button variant="outline" size="sm" disabled={busy}
                    onClick={() => persistReqs(d.requirements, true)}
                    data-testid="ops-billing-save-gc-default">
              Save as this GC's default
            </Button>
          )}
        </div>
        <table className="w-full text-sm" data-testid="ops-billing-requirements">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-1 text-left">On</th>
              <th className="px-2 py-1 text-left">Requirement</th>
              <th className="px-2 py-1 text-left">When</th>
              <th className="px-2 py-1 text-left">Hard stop</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {d.requirement_types.map((t) => {
              const on = d.requirements.find((r) => r.key === t.key);
              return (
                <tr key={t.key} data-testid={`ops-billing-req-${t.key}`}>
                  <td className="px-2 py-1.5">
                    <Checkbox checked={!!on} disabled={!d.can_edit}
                              data-testid={`ops-billing-req-on-${t.key}`}
                              onCheckedChange={() => toggleReq(t.key, "on")} />
                  </td>
                  <td className="px-2 py-1.5">
                    {t.label}
                    {t.requires_notarization && (
                      <span className="ml-1 text-[10px] uppercase text-slate-400">notary</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {on ? (
                      <select value={on.cadence} disabled={!d.can_edit}
                              data-testid={`ops-billing-req-cadence-${t.key}`}
                              onChange={(e) => toggleReq(t.key, "cadence", e.target.value)}
                              className="rounded-md border border-slate-200 px-1.5 py-0.5 text-xs">
                        {d.cadences.map((c) => (
                          <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                      </select>
                    ) : <span className="text-xs text-slate-400">
                      {d.cadences.find((c) => c.key === t.cadence)?.label}
                    </span>}
                  </td>
                  <td className="px-2 py-1.5">
                    {on && (
                      <Checkbox checked={!!on.hard_stop} disabled={!d.can_edit}
                                data-testid={`ops-billing-req-stop-${t.key}`}
                                onCheckedChange={(v) => toggleReq(t.key, "hard_stop", !!v)} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <CalendarClock className="h-4 w-4 text-slate-400" /> Billing Calendar
          </h3>
          <Button variant="outline" size="sm" onClick={regenerate} disabled={busy}
                  data-testid="ops-billing-regenerate">
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Rebuild
          </Button>
        </div>
        {d.periods.length === 0 ? (
          <NeedNote testId="ops-billing-no-calendar">
            No billing periods yet — rebuild to lay them down from the contract dates.
          </NeedNote>
        ) : (
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-1 text-left">Period</th>
                  <th className="px-2 py-1 text-left">Through</th>
                  <th className="px-2 py-1 text-left">PM deadline</th>
                  <th className="px-2 py-1 text-left">GC due</th>
                  <th className="px-2 py-1 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {d.periods.map((r) => (
                  <tr key={r.id} data-testid={`ops-billing-period-${r.period_end}`}
                      className={r.period_end === d.current_period?.period_end
                        ? "bg-blue-50/60" : ""}>
                    <td className="px-2 py-1.5 font-medium text-slate-800">{r.label}</td>
                    <td className="px-2 py-1.5 tabular-nums">{r.period_end}</td>
                    <td className="px-2 py-1.5 tabular-nums">{r.internal_deadline}</td>
                    <td className="px-2 py-1.5 tabular-nums">{r.gc_submission_date}</td>
                    <td className="px-2 py-1.5 text-xs capitalize text-slate-500">
                      {r.status.replace(/_/g, " ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
